import {FlowlineCommands} from '../../commands/flowline/flowline.commands';
import {computed, Injectable, NgZone} from '@angular/core';
import {
  hasDrilldown,
  hasDTAttachment,
  isActivity,
  isConnector,
  isExtended,
  isFlowline,
  isImage,
  isSticker,
  isTerminator,
  isText,
  hasAttachmentOfType,
  hasLinkedDiagram,
  hasDrilldownOrLinkedDiagram
} from '../../diagram-render/helpers/shapes-detectors.util';
import {ActionsHub} from './actions-hub';
import {FlowlineHelperService} from '../../diagram-render/helpers/flowline-helper.service';
import {DiagramJsonHandlerService, IActJson} from '../../diagram-data/diagram-json-handler.service';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {getNewConnectionTerminalPoint} from '../../diagram-render/helpers/get-connection-points.util';
import {ConnectorCommands} from '../../commands/connector/connector.commands';
import {UndoService} from '../../commands/undo.service';
import {
  ConfirmDeleteDialogComponent,
  DialogType
} from '../../editor-interface/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import {GeneralCommands} from '../../commands/general/general.commands';
import {ActivityCommands} from '../../commands/activity/activity.commands';
import {ImageCommands} from '../../commands/image/image.commands';
import {TextCommands} from '../../commands/text/text.commands';
import {StickerCommands} from '../../commands/sticky-note/sticker.commands';
import {ImageUploadService} from '../../core/services/image.upload.service';
import {DrilldownService} from '../services/drilldown.service';
import * as _ from 'lodash';
import {AuthService} from '../../core/auth/auth.service';
import {ResourceCommands} from '../../commands/resource/resource.commands';
import {RenumberService} from '../services/renumber.service';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {ConfirmDialogType} from '../../editor-interface/dialogs/confirm-dialog/confirm-dialog.component';
import {AttachmentsService} from '../attachments/attachments.service';
import {StickersService} from '../services/stickers.service';
import {SidebarService} from '../../editor-interface/sidebar/sidebar.service';
import {FlowlineAssociationService} from '../../core/services/flowline-association.service';
import {StyleClipboardService} from '../services/style-clipboard.service';
import {CopyCellsService} from '../../core/services/copy-cells.service';
import {MapService} from '../../core/map/map.service';
import {ChangeLinePointDialogComponent} from '../../editor-interface/dialogs/change-line-point-dialog/change-line-point-dialog.component';
import {SfaHeaderService} from '../../diagram-render/sfa-header/sfa-header.service';
import {ChangeStickerColorDialogComponent} from '../../editor-interface/dialogs/change-sticker-color-dialog/change-sticker-color-dialog.component';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';
import {GenerateStoriesAiDialogComponent} from 'src/app/editor-interface/dialogs/generate-stories-ai-dialog/generate-stories-ai-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {ChangeFontDialogComponent} from 'src/app/editor-interface/dialogs/change-font-dialog/change-font-dialog.component';
import {ShapesTemplateService} from 'src/app/diagram-render/shapes/shapes-template.service';
import {ChangeTagsDialogComponent} from 'src/app/editor-interface/dialogs/change-tags-dialog/change-tags-dialog.component';
import {
  CANVAS_TOP_MARGIN,
  LEFT_TOOLBAR_WIDTH
} from 'src/app/editor-interface/toolbar/util/constants';
import {DialogWidth, PermissionsService} from '@q9elements/ui-kit/common';
import {AddResourceDialogComponent} from 'src/app/editor-interface/dialogs/add-resource-dialog/add-resource-dialog.component';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';
import {ActivityTabsInEditEnum} from '../../diagram-render/shapes/activity-shape/shape.model';
import {drilldownConfigs, DrilldownConfig} from '../../editor-interface/shared/models/map.model';
import {
  PositionType,
  CanvasElementType
} from '../../diagram-render/styles/diagram-type-styles.model';

declare let mxUtils: any; // available globally
declare let mxEventSource: any; // available globally
declare let mxConstants: any; // available globally
declare let mxEvent: any; // available globally

@Injectable()
export class GraphActionsService {
  ui: any;
  editor: any;
  graph: any;
  hub: ActionsHub;
  isPro: () => boolean;
  isProTrial: () => boolean;
  isProTrialActive: () => boolean;
  expiredProIco = '../assets/icons/trial-expired.svg'; // TODO: move to constants
  trialExpiredMsg = 'Pro trial expired';
  private notAvailableInPlaygroundMessage = this.translateService.instant(
    'GPT_STORIES.NOT_AVAILABLE'
  );
  private notAvailableOnPlanMsg = this.translateService.instant('HINTS.NOT_AVAILABLE_ON_PLAN');
  private notAvailableOnCurrentPlanMsg = this.translateService.instant(
    'BILLING.NOT_AVAILABLE_ON_PLAN'
  );
  private gtpFlagTurnedOffMsg = this.translateService.instant('GPT_STORIES.FLAG_TURNED_OFF');
  private notAvailableChangesLicenseMsg = this.translateService.instant(
    'CORE.CHANGES.NO_LICENSE_TITLE'
  );
  private notAvailableDataTablesLicenseMsg = this.translateService.instant(
    'CORE.ATTACHMENTS.DATA_TABLES_NOT_AVAILABLE'
  );

  constructor(
    private graphHelper: GraphHelperService,
    private flowlineHelper: FlowlineHelperService,
    private jsonHandler: DiagramJsonHandlerService,
    private connectorCommands: ConnectorCommands,
    private generalCommands: GeneralCommands,
    private activityCommands: ActivityCommands,
    private flowlineCommands: FlowlineCommands,
    private imageCommands: ImageCommands,
    private textCommands: TextCommands,
    private stickerCommands: StickerCommands,
    private undoSvc: UndoService,
    private imageUploadSvc: ImageUploadService,
    private drilldownSvc: DrilldownService,
    private resourceCommands: ResourceCommands,
    private authSvc: AuthService,
    private popupSvc: PopupManagerService,
    private renumberSvc: RenumberService,
    private stickersSvc: StickersService,
    private sidebarSvc: SidebarService,
    private flAssoc: FlowlineAssociationService,
    private attachmentsSvc: AttachmentsService,
    private styleClipboard: StyleClipboardService,
    private ngZone: NgZone,
    private copySvc: CopyCellsService,
    private headerSvc: SfaHeaderService,
    private mapService: MapService,
    private permissionsService: PermissionsService,
    private translateService: TranslateService,
    private shapesTemplateService: ShapesTemplateService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  public init(editorUi) {
    this.ui = editorUi;
    this.editor = this.ui.editor;
    this.graph = this.editor.graph;
    this.hub = new ActionsHub();

    this.isPro = this.jsonHandler.isPro.bind(this.jsonHandler);
    this.isProTrial = this.jsonHandler.isProTrial.bind(this.jsonHandler);
    this.isProTrialActive = this.jsonHandler.isProTrialActive.bind(this.jsonHandler);

    this.setupCanvasActions();
    this.setupVertexActions();
    this.setupFlowlineActions();

    return this.hub;
  }

  private setupFlowlineActions() {
    this.hub
      .addAction('addActivity...', () => {
        const selectedCells = this.graph.selectionModel.cells.slice(0);
        const isSFA = this.mapService.isSFA();

        this.graph.model.beginUpdate();

        for (let i = selectedCells.length; i--; ) {
          const selectedCell = selectedCells[i],
            geometry = selectedCell.geometry;
          let deltaX, deltaY;
          const {w: vertexWidth, h: vertexHeight} =
            this.diagramTypeConfigService.activitySettings().defaultActivityShape;
          let x = selectedCell.target
            ? Math.round(selectedCell.geometry.sourcePoint.x + 0 / 2)
            : Math.round(selectedCell.geometry.targetPoint.x + 0 / 2);
          let y = selectedCell.target
            ? Math.round(selectedCell.geometry.sourcePoint.y + selectedCell.geometry.height / 2)
            : Math.round(selectedCell.geometry.targetPoint.y + selectedCell.geometry.height / 2);
          const dataCell = selectedCell.target ? selectedCell.target : selectedCell.source;
          const newConnectionTerminalPoint = getNewConnectionTerminalPoint(dataCell, x, y);

          if (newConnectionTerminalPoint[0] === 0) {
            if (newConnectionTerminalPoint[1] === 0) {
              // Northwest

              deltaX = 0;
              deltaY = 0;
            } else if (newConnectionTerminalPoint[1] === 1) {
              // Southwest

              deltaX = 0;
              deltaY = vertexHeight;
            } else {
              // West

              deltaX = 0;
              deltaY = vertexHeight / 2;
            }
          } else if (newConnectionTerminalPoint[0] === 1) {
            if (newConnectionTerminalPoint[1] === 0) {
              // Northeast

              deltaY = 0;
              deltaX = vertexWidth;
            } else if (newConnectionTerminalPoint[1] === 1) {
              // Southeast

              deltaY = vertexHeight;
              deltaX = vertexWidth;
            } else {
              // East

              deltaX = vertexWidth;
              deltaY = vertexHeight / 2;
            }
          } else {
            if (newConnectionTerminalPoint[1] === 0) {
              // North

              deltaX = vertexWidth / 2;
              deltaY = 0;
            } else {
              // South

              deltaX = vertexWidth / 2;
              deltaY = vertexHeight;
            }
          }

          x -= deltaX;
          y -= deltaY;

          const number = this.graphHelper.getFreeNumber(2);
          const terminalPrefix = !selectedCell.target ? 'entry' : 'exit';
          const connectedFlowline = {
            isSource: !!selectedCell.target,
            mxKey: +selectedCell.id,
            xTerminalKey: terminalPrefix + 'X',
            yTerminalKey: terminalPrefix + 'Y',
            xTerminalValue: newConnectionTerminalPoint[0],
            yTerminalValue: newConnectionTerminalPoint[1]
          };

          const {text, footerText, defaultResources, defaultAttributes, attributePadding} =
            this.diagramTypeConfigService.activitySettings(x, y);

          const activityData = {
            number,
            text,
            position: {x: x, y: y, w: vertexWidth, h: vertexHeight},
            connectedFlowlines: [connectedFlowline],
            attributePadding,
            ...(footerText && {footerText}),
            ...(defaultResources && {resources: defaultResources}),
            ...(defaultAttributes && {attributes: defaultAttributes})
          };

          const createCommand = this.activityCommands.create(activityData);

          this.undoSvc.executeCommand(createCommand);
        } // for selected cells

        this.graph.model.endUpdate();
        this.graph.selectionModel.addCells(selectedCells);
      })
      .setDynamicLabel(
        () => this.diagramTypeConfigService.flowlineSettings().dynamicLabel
      ).isEnabled = () => {
      if (
        this.diagramTypeConfigService
          .generalSettings()
          .disabledCanvasElements?.includes(CanvasElementType.ACTIVITY)
      ) {
        return false;
      }

      const selection = this.graph.getSelectionCells();

      // must be only 1 selected cell
      if (selection.length > 1) {
        return false;
      }

      // must be flowline
      if (!this.graph.model.isEdge(selection[0])) {
        return false;
      }

      // source or target should be null
      if (
        (selection[0].source && selection[0].target) ||
        (!selection[0].source && !selection[0].target)
      ) {
        return false;
      }

      return this.graph.isEnabled();
    };

    this.hub.addAction('addTerminator...', evt => {
      const selectedCells = this.graph.selectionModel.cells.slice(0);

      this.graph.model.beginUpdate();

      for (let i = selectedCells.length; i--; ) {
        const selectedCell = selectedCells[i],
          geometry = selectedCell.geometry;
        let deltaX, deltaY;
        const vertexWidth = 16,
          vertexHeight = 16;
        let x = selectedCell.target
          ? selectedCell.geometry.sourcePoint.x
          : selectedCell.geometry.targetPoint.x;
        let y = selectedCell.target
          ? selectedCell.geometry.sourcePoint.y
          : selectedCell.geometry.targetPoint.y;
        const dataCell = selectedCell.target ? selectedCell.target : selectedCell.source;
        const newConnectionTerminalPoint = getNewConnectionTerminalPoint(dataCell, x, y);

        if (newConnectionTerminalPoint[0] === 0) {
          if (newConnectionTerminalPoint[1] === 0) {
            // Northwest

            deltaX = 0;
            deltaY = 0;
          } else if (newConnectionTerminalPoint[1] === 1) {
            // Southwest

            deltaX = 0;
            deltaY = vertexHeight;
          } else {
            // West

            deltaX = 0;
            deltaY = vertexHeight / 2;
          }
        } else if (newConnectionTerminalPoint[0] === 1) {
          if (newConnectionTerminalPoint[1] === 0) {
            // Northeast

            deltaY = 0;
            deltaX = vertexWidth;
          } else if (newConnectionTerminalPoint[1] === 1) {
            // Southeast

            deltaY = vertexHeight;
            deltaX = vertexWidth;
          } else {
            // East

            deltaX = vertexWidth;
            deltaY = vertexHeight / 2;
          }
        } else {
          if (newConnectionTerminalPoint[1] === 0) {
            // North

            deltaX = vertexWidth / 2;
            deltaY = 0;
          } else {
            // South

            deltaX = vertexWidth / 2;
            deltaY = vertexHeight;
          }
        }

        x -= deltaX;
        y -= deltaY;

        const connectorData = {
          position: {x: x, y: y, w: vertexWidth, h: vertexHeight},
          flowlineMxKey: Number(selectedCell.id),
          newConnectionTerminalPoint: newConnectionTerminalPoint
        };

        const createCommand = this.connectorCommands.create(connectorData, 'terminator');

        this.undoSvc.executeCommand(createCommand);
      } // for selected cells

      this.graph.model.endUpdate();
      this.graph.selectionModel.addCells(selectedCells);
    }).isEnabled = () => {
      let result = true;
      const selection = this.graph.getSelectionCells();

      for (let i = selection.length; i-- && result; ) {
        if (!this.graph.model.isEdge(selection[i])) {
          result = false;
          continue;
        }

        const isSourceAndTargetNotExists = !selection[i].source && !selection[i].target;
        const isSourceAndTargetExists = selection[i].source && selection[i].target;
        const isSourceOrTargetTerminator =
          isTerminator(selection[i].source) || isTerminator(selection[i].target);
        const isSourceOrTargetConnector =
          (selection[i].source && isConnector(selection[i].source)) ||
          (selection[i].target && isConnector(selection[i].target));

        if (
          isSourceAndTargetNotExists ||
          isSourceAndTargetExists ||
          isSourceOrTargetTerminator ||
          isSourceOrTargetConnector
        ) {
          result = false;
        }
      }

      return this.graph.isEnabled() && selection.length && selection.length === 1 && result;
    };

    this.hub.addAction('addConnector...', evt => {
      const selectedCells = this.graph.selectionModel.cells.slice(0);

      this.graph.model.beginUpdate();

      for (let i = selectedCells.length; i--; ) {
        const selectedCell = selectedCells[i],
          geometry = selectedCell.geometry;
        let deltaX, deltaY;
        const vertexWidth = 16,
          vertexHeight = 16;
        let x = selectedCell.target
          ? selectedCell.geometry.sourcePoint.x
          : selectedCell.geometry.targetPoint.x;
        let y = selectedCell.target
          ? selectedCell.geometry.sourcePoint.y
          : selectedCell.geometry.targetPoint.y;
        const dataCell = selectedCell.target ? selectedCell.target : selectedCell.source;
        const newConnectionTerminalPoint = getNewConnectionTerminalPoint(dataCell, x, y);

        if (newConnectionTerminalPoint[0] === 0) {
          if (newConnectionTerminalPoint[1] === 0) {
            // Northwest

            deltaX = 0;
            deltaY = 0;
          } else if (newConnectionTerminalPoint[1] === 1) {
            // Southwest

            deltaX = 0;
            deltaY = vertexHeight;
          } else {
            // West

            deltaX = 0;
            deltaY = vertexHeight / 2;
          }
        } else if (newConnectionTerminalPoint[0] === 1) {
          if (newConnectionTerminalPoint[1] === 0) {
            // Northeast

            deltaY = 0;
            deltaX = vertexWidth;
          } else if (newConnectionTerminalPoint[1] === 1) {
            // Southeast

            deltaY = vertexHeight;
            deltaX = vertexWidth;
          } else {
            // East

            deltaX = vertexWidth;
            deltaY = vertexHeight / 2;
          }
        } else {
          if (newConnectionTerminalPoint[1] === 0) {
            // North

            deltaX = vertexWidth / 2;
            deltaY = 0;
          } else {
            // South

            deltaX = vertexWidth / 2;
            deltaY = vertexHeight;
          }
        }

        x -= deltaX;
        y -= deltaY;

        const connectorData = {
          position: {x: x, y: y, w: vertexWidth, h: vertexHeight},
          flowlineMxKey: Number(selectedCell.id),
          newConnectionTerminalPoint: newConnectionTerminalPoint
        };

        const createCommand = this.connectorCommands.create(connectorData, 'connector');

        this.undoSvc.executeCommand(createCommand);
      } // for selected cells

      this.graph.model.endUpdate();
      this.graph.selectionModel.addCells(selectedCells);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      // must be only 1 selected cell
      if (selection.length > 1) {
        return false;
      }

      // must be flowline
      if (!this.graph.model.isEdge(selection[0])) {
        return false;
      }

      // source or target should be null
      if (
        (selection[0].source && selection[0].target) ||
        (!selection[0].source && !selection[0].target)
      ) {
        return false;
      }

      // source or target should be an activity cell
      const isSourceActivity = selection[0].source && isActivity(selection[0].source);
      const isTargetActivity = selection[0].target && isActivity(selection[0].target);

      if (!isSourceActivity && !isTargetActivity) {
        return false;
      }

      return this.graph.isEnabled();
    };

    this.hub.addAction('reverseLine', () => {
      const cell = this.graph.getSelectionCells()[0];

      this.flowlineHelper.reverseLine(cell);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        selection[0].edge &&
        !isConnector(selection[0].target) &&
        !isConnector(selection[0].source)
      );
    };

    const changePoints = () => {
      return this.popupSvc.open(ChangeLinePointDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-line-point'],
        disableClose: false,
        backdropClass: 'none',
        data: {cell: this.graph.getSelectionCells()[0]}
      });
    };

    this.hub.addAction('choosePoints', () => changePoints());

    this.hub.addAction('makeStraight', () => {
      const cell = this.graph.getSelectionCells()[0];

      this.flowlineHelper.makeStraight(cell, true);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return this.graph.isEnabled() && selection.length === 1 && selection[0].edge;
    };

    this.hub.addAction('addStartText', () => {
      const cell = this.graph.getSelectionCells()[0];
      const commandData = {
        flowlineMxKey: cell.id,
        key: 'start',
        text: 'Start text',
        position: this.flowlineHelper.getSmartLineTextPosition(cell, true)
      };
      const command = this.flowlineCommands.addLineText(commandData);

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const cell = selection[0];
      const isSingleLine = selection.length === 1 && cell.edge;
      const canAddText =
        isSingleLine && !cell.getJson()?.lineTexts?.some(item => item.key === 'start');

      return (
        this.graph.isEnabled() &&
        this.diagramTypeConfigService.flowlineSettings().canAddStartText &&
        canAddText
      );
    };

    this.hub.addAction('addEndText', () => {
      const cell = this.graph.getSelectionCells()[0];
      const commandData = {
        flowlineMxKey: cell.id,
        key: 'end',
        text: 'End text',
        position: this.flowlineHelper.getSmartLineTextPosition(cell, false)
      };
      const command = this.flowlineCommands.addLineText(commandData);

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const cell = selection[0];
      const isSingleLine = selection.length === 1 && cell.edge;
      const canAddText =
        isSingleLine && !cell.getJson()?.lineTexts?.some(item => item.key === 'end');

      return (
        this.graph.isEnabled() &&
        this.diagramTypeConfigService.flowlineSettings().canAddEndText &&
        canAddText
      );
    };

    this.hub.addAction('addInteraction', () => {
      const cell = this.graph.getSelectionCells()[0];
      const commandData = {
        flowlineMxKey: cell.id,
        description: '',
        offset: {x: 0, y: 5}
      };
      const command = this.flowlineCommands.addInteraction(commandData);

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const cell = selection[0];
      const isSingleLine = selection.length === 1 && cell.edge;
      const alreadyExist = (this.jsonHandler.get().interactions || []).find(
        ia => Number(ia.flowlineMxKey) === Number(cell.id)
      );

      return (
        this.graph.isEnabled() &&
        this.diagramTypeConfigService.flowlineSettings().canAddInteraction &&
        isSingleLine &&
        !alreadyExist
      );
    };

    this.hub.addAction('deleteInteraction', () => {
      const cell = this.graph.getSelectionCells()[0];
      const commandData = {flowlineMxKey: cell.id};
      const command = this.flowlineCommands.deleteInteraction(commandData);

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const cell = selection[0];
      const isSingleLine = selection.length === 1 && cell.edge;
      const alreadyExist = (this.jsonHandler.get().interactions || []).find(
        ia => Number(ia.flowlineMxKey) === Number(cell.id)
      );

      return (
        this.graph.isEnabled() &&
        this.diagramTypeConfigService.flowlineSettings().canAddInteraction &&
        isSingleLine &&
        alreadyExist
      );
    };

    this.hub.addAction('formatLineStyle', () => {
      if (!this.sidebarSvc.rightSidebar.opened) {
        this.sidebarSvc.toggle('right');
      }
    }).isEnabled = function () {
      return true;
    };

    this.hub.addAction('clearWaypoints', () => {
      const cells = this.graph.getSelectionCells();

      if (cells != null) {
        this.graph.getModel().beginUpdate();

        try {
          for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];

            if (this.graph.getModel().isEdge(cell)) {
              let geo = this.graph.getCellGeometry(cell);

              if (geo != null) {
                geo = geo.clone();
                geo.points = null;
                this.graph.getModel().setGeometry(cell, geo);
              }
            }
          }
        } finally {
          this.graph.getModel().endUpdate();
        }
      }
    }).isEnabled = () => {
      const cell = this.graph.getSelectionCell();

      return cell && cell.edge && cell.geometry.points && cell.geometry.points.length > 0;
    };

    this.hub.addAction('formatConnectorThemes', () => {
      if (!this.sidebarSvc.rightSidebar.opened) {
        this.sidebarSvc.toggle('right');
      }
    }).isEnabled = function () {
      return true;
    };

    this.hub.addAction('formatTerminatorThemes', () => {
      if (!this.sidebarSvc.rightSidebar.opened) {
        this.sidebarSvc.toggle('right');
      }
    }).isEnabled = function () {
      return true;
    };

    this.hub.addAction('copyConnectorLink', () => {
      const selection = this.graph.getSelectionCells(),
        cell = selection[0];

      if (isConnector(cell)) {
        this.flAssoc.copy(cell);
      }
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells(),
        cell = selection[0];

      return isConnector(cell);
    };

    this.hub.addAction('pasteConnectorLink', () => {
      const selection = this.graph.getSelectionCells(),
        cell = selection[0];

      if (isConnector(cell)) {
        this.flAssoc.connect(cell);
      }
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells(),
        cell = selection[0];

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        this.flAssoc.canPaste(cell) &&
        isConnector(cell)
      );
    };
  }

  private setupCanvasActions() {
    this.hub.addAction(
      'zoomIn',
      () => {
        this.zoomIn();
      },
      true
    );
    this.hub.addAction(
      'zoomOut',
      () => {
        this.zoomOut();
      },
      true
    );

    this.hub.addAction(
      'fitWindow',
      () => {
        this.fit();
      },
      null
    ).isEnabled = () => {
      return this.canvasNotEmpty;
    };

    this.hub.addAction('hideStickers', () => {
      this.stickersSvc.switchVisible();
    }).isEnabled = () => {
      const notesExist = this.graphHelper.allStickers().length > 0;

      return notesExist && this.stickersSvc.isLayerVisible;
    };

    this.hub.addAction('showStickers', () => {
      this.stickersSvc.switchVisible(true, true);
    }).isEnabled = () => {
      const notesExist = this.graphHelper.allStickers().length > 0;

      return notesExist && !this.stickersSvc.isLayerVisible;
    };

    this.hub.addAction('filterStickersByUser', () => {
      this.popupSvc.openFilterNotesDialog('author');
    }).isEnabled = () => {
      const notesExist = this.graphHelper.allStickers().length > 0;

      return notesExist && this.stickersSvc.isLayerVisible && !this.popupSvc.filterNotesDialogRef;
    };

    this.hub.addAction('filterStickersByColor', () => {
      this.popupSvc.openFilterNotesDialog('paperClass');
    }).isEnabled = () => {
      const notesExist = this.graphHelper.allStickers().length > 0;

      return notesExist && this.stickersSvc.isLayerVisible && !this.popupSvc.filterNotesDialogRef;
    };

    this.hub.addAction('filterStickersByTag', () => {
      this.popupSvc.openFilterNotesDialog('tags');
    }).isEnabled = () => {
      const notesExist = this.graphHelper.allStickers().length > 0;

      return (
        notesExist &&
        this.stickersSvc.isLayerVisible &&
        !this.popupSvc.filterNotesDialogRef &&
        this.jsonHandler.get().notes.some(note => note.tags?.length > 0)
      );
    };

    this.hub.addAction(
      'selectVertices',
      () => {
        this.graph.selectVertices();
      },
      null
    ).isEnabled = () => {
      const vertices = [
        CanvasElementType.ACTIVITY,
        CanvasElementType.TEXT,
        CanvasElementType.STICKY_NOTE,
        CanvasElementType.IMAGE
      ];

      return (
        this.graph.isEnabled() &&
        vertices.some(
          v => !this.diagramTypeConfigService.generalSettings().disabledCanvasElements?.includes(v)
        )
      );
    };

    this.hub.addAction(
      'selectBoxes',
      () => {
        this.graph.selectActivities();
      },
      null
    ).isEnabled = () =>
      this.graph.isEnabled() &&
      !this.diagramTypeConfigService
        .generalSettings()
        .disabledCanvasElements?.includes(CanvasElementType.ACTIVITY);

    this.hub.addAction(
      'selectEdges',
      () => {
        this.graph.selectEdges();
      },
      null
    ).isEnabled = () =>
      this.graph.isEnabled() &&
      !this.diagramTypeConfigService
        .generalSettings()
        .disabledCanvasElements?.includes(CanvasElementType.FLOWLINE);

    this.hub.addAction(
      'selectAll',
      () => {
        this.stickersSvc.switchVisible(true, true);
        this.graph.selectAll();
      },
      null,
      null,
      null,
      'Ctrl+A'
    ).isEnabled = () => this.graph.isEnabled();

    this.hub.addAction('makeAllLinesStraight', () => {
      this.graph.model.beginUpdate();
      this.graphHelper.allFlowlines().forEach(c => this.flowlineHelper.makeStraight(c));
      this.graph.model.endUpdate();
    }).isEnabled = () => {
      return this.graph.isEnabled() && this.graphHelper.allFlowlines().length > 0;
    };

    this.hub.addAction(
      'addImage',
      evt => {
        this.graph.popupMenuHandler.hideMenu();
        this.imageUploadSvc.createImage(evt);
      },
      null
    ).isEnabled = () => {
      if (
        this.diagramTypeConfigService
          .generalSettings()
          .disabledCanvasElements?.includes(CanvasElementType.IMAGE)
      ) {
        return false;
      }

      return this.graph.isEnabled() && this.jsonHandler.isPro();
    };

    this.hub.addAction(
      'addSticker',
      evt => {
        this.graph.popupMenuHandler.hideMenu();
        const pt = mxUtils.convertPoint(
          this.graph.container,
          mxEvent.getClientX(evt),
          mxEvent.getClientY(evt)
        );
        const x = pt.x / this.graph.view.scale - this.graph.view.translate.x;
        const y = pt.y / this.graph.view.scale - this.graph.view.translate.y;
        const command = this.stickerCommands.create(
          this.diagramTypeConfigService.stickerNoteSettings.preparePayload({
            x,
            y,
            mxKey: -Date.now(), // this is hack to avoid mxKey overlap during simultanious editing
            position: PositionType.CENTER
          })
        );

        this.undoSvc.executeCommand(command);
      },
      null
    ).isEnabled = () => {
      return !this.graph.isEnabled();
    };

    this.hub
      .addAction(
        'copy',
        () => {
          return this.enabledCopyActivity() && this.copySvc.copy();
        },
        this.enabledCopyActivity,
        null,
        this.showCopyImageNavIcon
      )
      .setRowTitle(this.getProTrialRowTooltipForCopy).isEnabled = () => {
      let enabled = false;
      const cells = this.graph.getSelectionCells();

      if (_.isEmpty(cells)) {
        return enabled;
      }

      const isImg = this.isImage();
      const isPro = this.jsonHandler.isProActive();
      const isProTrial = this.jsonHandler.isProTrial();
      const isProTrialActive = this.jsonHandler.isProTrialActive();

      if (isImg && (isPro || (isProTrial && !isProTrialActive))) {
        enabled = true;
      } else if (!isImg) {
        enabled = true;
      }

      return enabled;
    };

    this.hub.addAction(
      'paste',
      evt => {
        this.copySvc.paste(evt);
      },
      null
    );

    this.hub.addAction('smartRenumber', () => {
      this.popupSvc.openConfirmDialog(
        this.renumberSvc,
        this.renumberSvc.doSmartRenumber,
        ConfirmDialogType.SMART_REN
      );
    }).isEnabled = () => {
      const activities = this.graphHelper.allActivities();
      const visibleNumber = activities.some(activity => !activity.comp.instance.hiddenNumber);

      return (
        this.graph.isEnabled() && activities.length > 1 && this.jsonHandler.isPro() && visibleNumber
      );
    };
  }

  private setupVertexActions() {
    const lockUnlockAction = () => {
      this.graph.getModel().beginUpdate();

      try {
        const selection = this.graph.getSelectionCells() || [];

        if (selection.length > 0) {
          const lockable = selection.filter(cell => {
            return this.graphHelper.isCellLockable(cell);
          });

          if (lockable.length > 0) {
            this.graph.toggleCellStyles(mxConstants.STYLE_LOCKED, 0, lockable);
          }
        }
      } finally {
        this.graph.getModel().endUpdate();
      }
    };

    const checkForLockState =
      (lock, skip = false) =>
      () => {
        const selection = this.graph.getSelectionCells() || [];
        const length = selection.length;
        let enabled = false;
        let hasLocked = false;

        if (length <= 0) {
          return enabled;
        }

        for (let i = 0; i < length; i++) {
          const cell = selection[i];
          const json = cell.getJson();

          hasLocked = json.locked;

          if (this.graphHelper.isCellLockable(cell)) {
            enabled = true;
            break;
          }
        }

        const active = skip ? true : lock ? !hasLocked : hasLocked;

        return this.graph.isEnabled() && enabled && active;
      };

    const {
      isRequirementsManager,
      isProActive,
      canGenerateChanges,
      hasChangeTicketsLicense,
      hasDataTablesLicense
    } = this.permissionsService;
    const canGenerateRequirements = computed(
      () => this.diagramTypeConfigService.generalSettings().canGenerateRequirements
    );
    const canGenerateStoriesAI = computed(
      () => this.diagramTypeConfigService.generalSettings().canGenerateStoriesAI
    );

    this.hub.addAction('lockUnlock', lockUnlockAction, null).isEnabled = checkForLockState(
      true,
      true
    );
    this.hub.addAction('lock', lockUnlockAction, null).isEnabled = checkForLockState(true);
    this.hub.addAction('unlock', lockUnlockAction, null).isEnabled = checkForLockState(false);

    this.hub.addAction('toFront', () => {
      this.graph.orderCells(false);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.every(isActivity) &&
        this.diagramTypeConfigService.activitySettings().canPlaceFrontBack
      );
    };

    this.hub.addAction('toBack', () => {
      this.graph.orderCells(true);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.every(isActivity) &&
        this.diagramTypeConfigService.activitySettings().canPlaceFrontBack
      );
    };

    this.hub.addAction(
      'delete',
      () => {
        this.deleteSelected();
      },
      null,
      'sprite-delete',
      null,
      'Delete'
    ).isEnabled = () => {
      return this.canDeleteSelected;
    };

    this.hub.addAction('addToLegend', () => {
      const cell = this.graph.getSelectionCells()[0];

      this.headerSvc.createLegendItemFromCell(cell);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const suitsLegend = isActivity(selection[0]) || isFlowline(selection[0]);

      return (
        this.graph.isEnabled() &&
        this.diagramTypeConfigService.generalSettings().canAddToLegend &&
        suitsLegend
      );
    };

    drilldownConfigs.forEach((config: DrilldownConfig) => this.registerDrilldownAction(config));

    this.hub.addAction('deleteDrilldown', () => {
      const cell = this.graph.getSelectionCells()[0];

      if (hasLinkedDiagram(cell)) {
        const command = this.activityCommands.removeLinkedDiagram({
          itemMxKey: cell.id,
          diagram: cell.getJson().linkedDiagram
        });

        this.undoSvc.executeCommand(command);
        this.updateContextBarOptions();

        return;
      }

      this.drilldownSvc.openDeleteDrilldownPopup(this.graph);
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        isActivity(selection[0]) &&
        hasDrilldownOrLinkedDiagram(selection[0])
      );
    };

    this.hub
      .addTrackableAction('importDrilldown', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.drilldownSvc.openImportDrilldownPopup(cell);
        },
        q9Track: 'CONTEXT_MENU.ACTIVITY.DRILLDOWN.IMPORT_DRILLDOWN'
      })
      .setRowTitle(() => this.translateService.instant('HINTS.IMPORT_DIAGRAM_DD_HINT')).isEnabled =
      () => {
        const selection = this.graph.getSelectionCells();

        return (
          this.graph.isEnabled() &&
          selection.length === 1 &&
          isActivity(selection[0]) &&
          !hasDrilldownOrLinkedDiagram(selection[0])
        );
      };

    this.hub.addTrackableAction('autoGenerateDrilldown', {
      funct: () => {
        const [cell] = this.graph.getSelectionCells();

        this.drilldownSvc.openAutoGenerateDrilldownPopup(cell);
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.DRILLDOWN.AUTO_GENERATE_DRILLDOWN'
    }).isEnabled = () => {
      const [cell] = this.graph.getSelectionCells();
      const {hasAccessToGPTFeatures} = this.permissionsService;

      return (
        cell &&
        this.graph.isEnabled() &&
        isActivity(cell) &&
        !hasDrilldownOrLinkedDiagram(cell) &&
        hasAccessToGPTFeatures() &&
        this.diagramTypeConfigService.generalSettings().canAutoGenerateDrilldown
      );
    };

    this.hub
      .addTrackableAction('sendToChild', {
        funct: () => {
          this.drilldownSvc.openSendToChildPopup(this.graph);
        },
        q9Track: 'CONTEXT_MENU.ACTIVITY.DRILLDOWN.SEND_TO_CHILD'
      })
      .setRowTitle(() => this.translateService.instant('HINTS.SEND_TO_CHILD_HINT')).isEnabled =
      () => {
        if (!this.diagramTypeConfigService.generalSettings().allowedDrilldownSubtypes?.length) {
          return false;
        }

        const selection = this.graph.getSelectionCells() || [];
        const length = selection.length;
        let result = true;

        for (let i = 0; i < length; i++) {
          const cell = selection[i];

          if (isConnector(cell) || isTerminator(cell)) {
            if (cell.edges && !_validateConnector(cell)) {
              result = false;
              break;
            }
          }
        }

        function _validateConnector(cell) {
          return _.some(selection, function (item) {
            return +item.id === +cell.edges[0].id;
          });
        }

        return this.graph.isEnabled() && length && result;
      };

    this.hub
      .addTrackableAction('useExistingDiagram', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.drilldownSvc.openUseExistingPopup(cell);
        },
        active: this.enableProFeature,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DRILLDOWN.USE_EXISTING_DIAGRAM'
      })
      .setRowTitle(this.getLinkExistingDiagramTooltip).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        isActivity(selection[0]) &&
        !hasDrilldownOrLinkedDiagram(selection[0])
      );
    };

    this.hub.addAction('exportDrilldown', () => {
      const cell = this.graph.getSelectionCell();

      if (hasDrilldown(cell)) {
        this.drilldownSvc.openExportDrilldownPopup(cell);
      }
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const canCopy = this.authSvc.getCanCopyMap();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        isActivity(selection[0]) &&
        hasDrilldown(selection[0]) &&
        canCopy
      );
    };

    this.hub.addTrackableAction('renumberActivity', {
      funct: () => {
        const cell = this.graph.getSelectionCells()[0];

        this.popupSvc.openActivityRenumberDialog(cell.id);
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.RENUMBER_ACTIVITY'
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const {hiddenNumber} = selection[0].comp?.instance || {};

      return this.graph.isEnabled() && !hiddenNumber;
    };

    this.hub.addAction('formatTextThemes', () => {
      this.sidebarSvc.selectedTabIndex['text'] = 0;

      if (!this.sidebarSvc.rightSidebar.opened) {
        this.sidebarSvc.toggle('right');
      }
    }).isEnabled = function () {
      return true;
    };

    this.hub
      .addAction('formatActivityThemes', () => {
        this.ngZone.run(() => {
          this.sidebarSvc.selectedTabIndex['activity'] =
            this.diagramTypeConfigService.activitySettings().selectedTabIndex;
          this.sidebarSvc.refresh(undefined, ActivityTabsInEditEnum.THEME);

          if (!this.sidebarSvc.rightSidebar.opened) {
            this.sidebarSvc.toggle('right');
          }
        });
      })
      .setDynamicLabel(
        () => this.diagramTypeConfigService.flowlineSettings().dynamicLabel && 'formatCardThemes'
      ).isEnabled = () => true;

    this.hub.addTrackableAction('attachNote', {
      funct: () => {
        const cell = this.graph.getSelectionCells()[0];

        this.attachmentsSvc.attachNote(cell);
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.ADD_NOTE'
    });

    this.hub.addTrackableAction('addAttribute', {
      funct: () => {
        const selection: MxCell[] = this.graph.getSelectionCells();

        const commands = selection.map(cell => {
          const num = ((cell.getJson?.() as any)?.attributes?.length || 0) + 1;

          return this.activityCommands.addAttribute({
            activityMxKey: cell.id,
            text: `Attribute ${num}`
          });
        });

        const groupCommand = this.generalCommands.group(commands);

        this.undoSvc.executeCommand(groupCommand);

        this.updateContextBarOptions();
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.BOX.ADD_ATTRIBUTE'
    }).isEnabled = () => {
      return this.diagramTypeConfigService.generalSettings().canAddAttribute;
    };

    this.hub.addAction('addFooterText', () => {
      const selection: MxCell[] = this.graph.getSelectionCells();

      const eligible = selection.filter(cell => !(cell.getJson() as IActJson)?.footerText);

      const commands = eligible.map(cell => this.activityCommands.addFooterText({mxKey: cell.id}));
      const groupCommand = this.generalCommands.group(commands);

      this.undoSvc.executeCommand(groupCommand);

      this.updateContextBarOptions();
    }).isEnabled = () => {
      const selection: MxCell[] = this.graph.getSelectionCells();

      return (
        this.diagramTypeConfigService.activitySettings().canAddFooterText &&
        selection.some(cell => !(cell.getJson() as IActJson)?.footerText)
      );
    };

    this.hub.addAction('makeContainerCard', () => {
      const cell = this.graph.getSelectionCells()[0];
      const command = this.activityCommands.changeContainer({mxKey: cell.id, container: true});

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      const isSFA = this.mapService.isSFA();

      return (
        !this.graph.getSelectionCells()[0].getJson().container &&
        this.diagramTypeConfigService.activitySettings().canMakeContainerCard
      );
    };

    this.hub.addAction('makeNormalCard', () => {
      const cell = this.graph.getSelectionCells()[0];
      const command = this.activityCommands.changeContainer({mxKey: cell.id, container: false});

      this.undoSvc.executeCommand(command);
    }).isEnabled = () => {
      return this.graph.getSelectionCells()[0].getJson().container;
    };

    this.hub
      .addTrackableAction('refModelNode', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachRefNodeLink(cell);
        },
        active: this.enableProFeature,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.PASTE_REF_MODEL_NODE'
      })
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && this.attachmentsSvc.canAttachRefNode(cell.getJson());
    };

    this.hub
      .addTrackableAction('orgModelNode', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachOrgModelNodes(cell);
        },
        active: this.enableProFeature,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.ADD_ORG_MODEL_NODE'
      })
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && this.attachmentsSvc.canAttachOrgModelNodes();
    };

    this.hub
      .addTrackableAction('copiedOrgModelNode', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachOrgNodeLink(cell, null);
        },
        active: this.enableProFeature,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.PASTE_ORG_MODEL_NODE'
      })
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && this.attachmentsSvc.canAttachCopiedOrgNode(cell.getJson());
    };

    this.hub
      .addTrackableAction('proposedNode', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachProposedNode(cell);
        },
        active: this.enableProFeature,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.CREATE_PROPOSED_NODE'
      })
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell);
    };

    this.hub.addTrackableAction('attachUrl', {
      funct: () => {
        const cell = this.graph.getSelectionCells()[0];

        this.attachmentsSvc.attachUrl(cell);
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.ADD_URL'
    });

    this.hub
      .addTrackableAction('attachImage', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachImage(cell);
        },
        active: this.enableProFeature,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.ADD_IMAGE'
      })
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && (this.jsonHandler.isPro() || this.jsonHandler.isProTrial());
    };

    this.hub
      .addTrackableAction('attachDataTable', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachDatatable(cell);
        },
        active: () => this.enableProFeature && hasDataTablesLicense(),
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.DOCUMENTATION.ADD_DATATABLE'
      })
      .setRowTitle(
        () => this.getProTrialRowTooltip() || this.getNoDataTablesLicenseTooltip()
      ).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && (this.jsonHandler.isPro() || this.jsonHandler.isProTrial());
    };

    this.hub
      .addTrackableAction('addRequirement', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.addRequirement(cell);
        },
        active: () => this.enableProFeature && hasChangeTicketsLicense(),
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.ADD_REQUIREMENT'
      })
      .setRowTitle(
        () => this.getProTrialRowTooltip() || this.getNoChangesLicenseTooltip()
      ).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && (this.jsonHandler.isPro() || this.jsonHandler.isProTrial());
    };

    this.hub
      .addTrackableAction('generateRequirement', {
        active: canGenerateChanges,
        funct: (): void => {
          this.attachmentsSvc.generateBusinessRequirementsFrom(
            this.graphHelper.selectedActivityIds
          );
        },
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.GENERATE_REQUIREMENT'
      })
      .setRowTitle(() => this.getGenerateChangesTooltipText()).isEnabled = () =>
      canGenerateRequirements() && isProActive() && isRequirementsManager();

    this.hub
      .addTrackableAction('generateRequirements', {
        active: canGenerateChanges,
        funct: (): void => {
          this.attachmentsSvc.generateBusinessRequirementsFrom(
            this.graphHelper.selectedActivityIds
          );
        },
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.GENERATE_REQUIREMENTS'
      })
      .setRowTitle(() => this.getGenerateChangesTooltipText()).isEnabled = () =>
      canGenerateRequirements() && isProActive() && isRequirementsManager();

    this.hub
      .addTrackableAction('addStory', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.addStory(cell);
        },
        active: () => this.enableProFeature && hasChangeTicketsLicense(),
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.STORY'
      })
      .setRowTitle(
        () => this.getProTrialRowTooltip() || this.getNoChangesLicenseTooltip()
      ).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return isActivity(cell) && (this.jsonHandler.isPro() || this.jsonHandler.isProTrial());
    };

    this.hub
      .addTrackableAction('pasteRequirementLink', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachRequirementLink(cell);
        },
        active: () => this.enableProFeature && hasChangeTicketsLicense(),
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.PASTE_REQUIREMENT'
      })
      .setRowTitle(
        () => this.getProTrialRowTooltip() || this.getNoChangesLicenseTooltip()
      ).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return (
        isActivity(cell) &&
        this.attachmentsSvc.canAttachRequirement(cell.getJson()) &&
        (this.jsonHandler.isPro() || this.jsonHandler.isProTrial())
      );
    };

    this.hub
      .addTrackableAction('pasteStoryLink', {
        funct: () => {
          const cell = this.graph.getSelectionCells()[0];

          this.attachmentsSvc.attachStoryLink(cell);
        },
        active: () => this.enableProFeature && hasChangeTicketsLicense(),
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.PASTE_STORY'
      })
      .setRowTitle(
        () => this.getProTrialRowTooltip() || this.getNoChangesLicenseTooltip()
      ).isEnabled = () => {
      const cell = this.graph.getSelectionCells()[0];

      return (
        isActivity(cell) &&
        this.attachmentsSvc.canAttachStory(cell.getJson()) &&
        (this.jsonHandler.isPro() || this.jsonHandler.isProTrial())
      );
    };

    this.hub
      .addAction(
        'addItemLink',
        () => {
          const cell = this.graph.getSelectionCell();

          if (cell) {
            this.popupSvc.openAttachURLDialog(cell);
          }
        },
        this.enableProFeature,
        null,
        this.showNavIcon
      )
      .setRowTitle(this.getProTrialRowTooltip).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        (isImage(selection[0]) || isText(selection[0])) &&
        (this.jsonHandler.isPro() || this.jsonHandler.isProTrial())
      );
    };

    this.hub.addAction('top', () => this.alignCells('top'));

    this.hub.addAction('bottom', () => this.alignCells('bottom'));

    this.hub.addAction('left', () => this.alignCells('left'));

    this.hub.addAction('right', () => this.alignCells('right'));

    this.hub.addAction('middleHorizontal', () => this.alignCells('horizontally'));

    this.hub.addAction('middleVertical', () => this.alignCells('vertically'));

    this.hub.addAction('vertically', () => this.distributeCells(false));

    this.hub.addAction('horizontally', () => this.distributeCells(true));

    this.hub.addAction('widthHeight', () => this.setSimilarSize(1, 1));

    this.hub.addAction('width', () => this.setSimilarSize(1, 0));

    this.hub.addAction('height', () => this.setSimilarSize(0, 1));

    this.hub
      .addTrackableAction('generateStoriesAI', {
        funct: () => {
          const selection: MxCell[] = this.graph.getSelectionCells();

          if (!selection.length) {
            return;
          }

          const activityMxKeys = selection.reduce((mxKeys, activity) => {
            if (isActivity(activity)) {
              mxKeys.push(activity.id);
            }

            return mxKeys;
          }, []);

          const diagramId = this.jsonHandler.get().diagramId;

          this.popupSvc.open(GenerateStoriesAiDialogComponent, {
            width: DialogWidth.MD,
            panelClass: 'q9-dialog',
            data: {
              diagramId,
              activityMxKeys
            }
          });
        },
        active: canGenerateChanges,
        shortcut: this.showNavIcon,
        q9Track: 'CONTEXT_MENU.ACTIVITY.CHANGE.GENERATE_STORIES_AI'
      })
      .setRowTitle(() => this.getGenerateChangesTooltipText()).isEnabled = () => {
      const selection: MxCell[] = this.graph.getSelectionCells();
      const selectionHasActivitiesOnly = selection.every(cell => isActivity(cell));
      const isEnabled = this.graph.isEnabled();
      const {isRequirementsManager} = this.permissionsService;

      return (
        canGenerateStoriesAI() && selectionHasActivitiesOnly && isEnabled && isRequirementsManager()
      );
    };

    this.hub.addTrackableAction('addResource', {
      funct: () => {
        const selection: MxCell[] = this.graph.getSelectionCells();

        if (!selection.length) {
          return;
        }

        const extendedCells = selection.reduce((acc, cell) => {
          if (isExtended(cell)) {
            const resources = _.cloneDeep(cell.getJson().resources) ?? [];

            acc = acc.concat(resources);
          }

          return acc;
        }, []);

        /** Find not shared resources **/
        const resMap = extendedCells.reduce((acc, curr) => {
          const key = String(curr.resourceDbId || curr.id);

          if (!key) {
            return acc;
          }

          if (!acc[key]) {
            curr.id = key;
            acc[key] = {i: 0, data: curr};
          }

          acc[key].i++;

          return acc;
        }, {});

        const attachedToAll = Object.keys(resMap).reduceRight((acc, curr) => {
          if (resMap[curr].i === selection.length) {
            acc.push(resMap[curr].data);
          }

          return acc;
        }, []);

        this.popupSvc
          .open(AddResourceDialogComponent, {
            width: DialogWidth.MD,
            panelClass: 'q9-dialog',
            data: {
              attachedResources: attachedToAll,
              teamId: this.jsonHandler.get().team.id,
              isPro: this.jsonHandler.isPro(),
              isArchitecture: this.diagramTypeConfigService.isArchitecture()
            }
          })
          .afterClosed()
          .toPromise()
          .then(result => {
            if (result && result.data && Array.isArray(result.data)) {
              const isMain = result.data.length === 1 && result.data[0].main;
              const commands = result.data.map(res =>
                this.resourceCommands.attach({
                  resource: res,
                  cells: selection,
                  position: 0,
                  skipCalc: isMain
                })
              );
              const groupCommand = this.generalCommands.group(commands);

              this.undoSvc.executeCommand(groupCommand);
            }
          });
      },
      q9Track: 'CONTEXT_MENU.ACTIVITY.BOX.ADD_RESOURCE'
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const {disableResources} = this.diagramTypeConfigService.generalSettings();

      return (
        this.graph.isEnabled() &&
        selection.length &&
        !selection.find(cell => !isActivity(cell)) &&
        !disableResources
      );
    };

    this.hub.addAction('resourcePlacement', () => {}).isEnabled = () =>
      this.diagramTypeConfigService.resourceSettings().resourcePlacement;

    const changeResourcePlacement = (perRow: number) => () => {
      const selection: any[] = this.graph.getSelectionCells();

      if (!selection.length) {
        return;
      }

      const filteredSelection = selection
        .filter(isExtended)
        .map(cell => cell.getJson())
        .filter(cellJson => (cellJson.resourcesPerRow ?? 0) !== perRow);

      if (filteredSelection.length) {
        const commands = filteredSelection.map(cellJson =>
          this.resourceCommands.changeResourcesPerRow({
            mxKey: cellJson.mxKey,
            resourcesPerRow: perRow
          })
        );
        const groupCommand = this.generalCommands.group(commands);

        this.undoSvc.executeCommand(groupCommand);
      }
    };

    const checkForResourcePlacementOption = (perRow: number) => () => {
      const selected = this.graph.getSelectionCells();

      if (selected.length === 1) {
        const cell = this.graph.getSelectionCells()[0];
        const cellJson = cell.getJson();

        return (
          this.diagramTypeConfigService.resourceSettings().resourcePlacement &&
          (cellJson.resourcesPerRow ?? 0) !== perRow
        ); // default 0;
      }

      return this.mapService.isSFA();
    };

    this.hub.addAction('resourcePlacementRow', changeResourcePlacement(0)).isEnabled =
      checkForResourcePlacementOption(0);
    this.hub.addAction('resourcePlacementTwo', changeResourcePlacement(2)).isEnabled =
      checkForResourcePlacementOption(2);
    this.hub.addAction('resourcePlacementFour', changeResourcePlacement(4)).isEnabled =
      checkForResourcePlacementOption(4);

    this.hub.addAction('pasteStyle', () => {
      const cells = this.graph.getSelectionCells();

      if (cells.length === 1) {
        this.styleClipboard.paste(cells[0]);
      } else if (cells.length > 1) {
        this.styleClipboard.multiPaste(cells);
      }
    }).isActive = () => {
      const selection = this.graph.getSelectionCells();

      if (!this.graph.isEnabled()) {
        return null;
      }

      if (selection.length === 1) {
        return this.styleClipboard.checkCanPaste(selection[0]);
      }

      if (this.jsonHandler.isPro()) {
        return _.some(selection, this.styleClipboard.checkCanPaste.bind(this.styleClipboard));
      }
    };

    this.hub.addAction('copyStyle', () => {
      const selection = this.graph.getSelectionCells();
      const cellJSON = selection[0]?.getJson();
      const styles = this.getCellStyle(selection);
      const type = this.styleClipboard._getType(selection[0]);

      if (styles) {
        if (type === 'flowline') {
          this.styleClipboard.copy(type, {...styles, line: {...cellJSON.flForm}});

          return;
        }

        this.styleClipboard.copy(type, styles);
      }
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();
      const styles = this.getCellStyle(selection);

      return Boolean(selection.length === 1 && styles);
    };

    this.hub.addAction('changeFont', () => {
      this.popupSvc.open(ChangeFontDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-change-font'],
        backdropClass: 'none',
        disableClose: false,
        data: {cell: this.graph.getSelectionCells()[0]}
      });
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return selection.length === 1 && isSticker(selection[0]);
    };

    this.hub.addAction('paperStyle', () => {
      this.popupSvc.open(ChangeStickerColorDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-change-color'],
        backdropClass: 'none',
        disableClose: false,
        data: {cell: this.graph.getSelectionCells()[0]}
      });
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return selection.length === 1 && isSticker(selection[0]);
    };

    this.hub.addAction('changeTags', () => {
      this.popupSvc.open(ChangeTagsDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-draggable', 'q9-change-tags'],
        backdropClass: 'none',
        disableClose: false,
        width: DialogWidth.SM,
        data: {cell: this.graph.getSelectionCells()[0]}
      });
    }).isEnabled = () => {
      const selection = this.graph.getSelectionCells();

      return selection.length === 1 && isSticker(selection[0]);
    };
  }

  setStickerTags(tags: string[], cell) {
    const newSticker = {...cell.getJson(), tags};

    if (isSticker(cell)) {
      const stickerValue = this.shapesTemplateService.sticker(newSticker);

      cell.tags = tags;
      this.graph.model.setValue(cell, stickerValue);
      this.graph.model.setVisible(cell, this.stickersSvc.isNoteTagsVisible(tags));
    }
  }

  setStickerStyle(value, mxKey?) {
    const cell = mxKey ? this.graph.model.getCell(mxKey) : this.graph.getSelectionCell();

    if (cell && isSticker(cell)) {
      this.graph.setCellStyles('paperClass', value, [cell]);
      this.graph.model.setVisible(cell, this.stickersSvc.isNoteColorVisible(value));
    }
  }

  private getCellStyle(selection) {
    return isSticker(selection[0])
      ? {paperClass: selection[0]?.getJson().paperClass}
      : selection[0]?.getJson()?.style;
  }

  get countSelectedVertices() {
    if (!this.graph) {
      return 0;
    }

    const cells = this.graph.getSelectionCells();
    let vertices = 0;

    cells.forEach(function (item) {
      if (item.vertex) {
        vertices++;
      }
    });

    return vertices;
  }

  alignCells(alignBy) {
    const directions = {
      left: mxConstants.ALIGN_LEFT,
      right: mxConstants.ALIGN_RIGHT,
      top: mxConstants.ALIGN_TOP,
      bottom: mxConstants.ALIGN_BOTTOM,
      horizontally: mxConstants.ALIGN_MIDDLE,
      vertically: mxConstants.ALIGN_CENTER
    };

    return this.graph && this.graph.alignCells(directions[alignBy]);
  }

  distributeCells(horizontal: boolean) {
    return this.graph && this.graph.distributeCells(horizontal);
  }

  setSimilarSize(useW, useH) {
    return this.graph && this.graph.similarSizeCells(useW, useH);
  }

  deleteSelected() {
    this.graph.popupMenuHandler.hideMenu();
    const cells = this.graph.getSelectionCells();

    if (this.graph.connectionHandler.isConnecting()) {
      this.graph.connectionHandler.reset();
    } else {
      const flags = [];

      if (cells.find(hasDTAttachment)) {
        flags.push(DialogType.DEL_DT);
      }

      if (cells.find(hasAttachmentOfType('text'))) {
        flags.push(DialogType.DEL_NOTE);
      }

      if (cells.find(hasAttachmentOfType('url'))) {
        flags.push(DialogType.DEL_URL);
      }

      if (cells.find(hasAttachmentOfType('image'))) {
        flags.push(DialogType.DEL_IMAGE);
      }

      if (cells.find(hasDrilldown)) {
        flags.push(DialogType.DEL_DD);
      }

      if (
        cells.find(
          cell => isConnector(cell) || isConnector(cell.source) || isConnector(cell.target)
        )
      ) {
        flags.push(DialogType.DEL_CONN);
      }

      const activities = cells.reduce((acc, cell) => {
        if (isActivity(cell)) {
          acc.push(this.activityCommands.delete(cell.getJson()));
        }

        return acc;
      }, []);
      const connTerm = cells.reduce((acc, cell) => {
        if (isConnector(cell)) {
          acc.push(this.connectorCommands.delete(cell.getJson(), 'connector'));
        } else if (isTerminator(cell)) {
          acc.push(this.connectorCommands.delete(cell.getJson(), 'terminator'));
        }

        return acc;
      }, []);
      const flowlines = cells.reduce((acc, cell) => {
        if (isFlowline(cell)) {
          if (
            cell.source &&
            cells.indexOf(cell.source) === -1 &&
            (isConnector(cell.source) || isTerminator(cell.source))
          ) {
            if (isConnector(cell.source)) {
              connTerm.push(this.connectorCommands.delete(cell.source.getJson(), 'connector'));
            } else if (isTerminator(cell.source)) {
              connTerm.push(this.connectorCommands.delete(cell.source.getJson(), 'terminator'));
            }
          }

          if (
            cell.target &&
            cells.indexOf(cell.target) === -1 &&
            (isConnector(cell.target) || isTerminator(cell.target))
          ) {
            if (isConnector(cell.target)) {
              connTerm.push(this.connectorCommands.delete(cell.target.getJson(), 'connector'));
            } else if (isTerminator(cell.target)) {
              connTerm.push(this.connectorCommands.delete(cell.target.getJson(), 'terminator'));
            }
          }

          const interaction = _.find(this.jsonHandler.get().interactions, ia => {
            return Number(ia.flowlineMxKey) === Number(cell.id);
          });

          if (interaction) {
            acc.push(this.flowlineCommands.deleteInteraction({flowlineMxKey: Number(cell.id)}));
          }

          acc.push(this.flowlineCommands.delete(cell.getJson()));
        }

        return acc;
      }, []);
      const other = cells.reduce((acc, cell) => {
        if (isImage(cell)) {
          acc.push(this.imageCommands.delete(cell.getJson()));
        } else if (isSticker(cell)) {
          acc.push(this.stickerCommands.delete(cell.getJson()));
        } else if (isText(cell)) {
          acc.push(this.textCommands.delete(cell.getJson()));
        }

        return acc;
      }, []);
      /** order is very important **/
      const commands = [].concat(connTerm, flowlines, activities, other);
      const groupCommand = this.generalCommands.group(commands);

      if (flags.length === 0) {
        this.undoSvc.executeCommand(groupCommand);
      } else {
        this.popupSvc
          .open(ConfirmDeleteDialogComponent, {
            panelClass: ['q9-dialog', 'q9-dialog-danger'],
            data: flags
          })
          .afterClosed()
          .subscribe(data => {
            if (data === true) {
              this.undoSvc.executeCommand(groupCommand);
              this.undoSvc.clear();
            }
          });
      }
    }
  }

  actualSize() {
    this.graph.zoomTo(1);
    this.ui.resetScrollbars();
  }

  zoomIn() {
    this.graph.zoomIn();
  }

  zoomOut() {
    this.graph.zoomOut();
  }

  fit() {
    const margin = CANVAS_TOP_MARGIN(this.mapService.isSFA() && this.headerSvc.isPinned);

    this.graph.fit(this.graph.getBorder(), false, margin);
    this.graph.adjustForVisibleToolbar(margin, LEFT_TOOLBAR_WIDTH);
  }

  enabledCopyActivity = () => {
    const cell = this.graph.getSelectionCells()[0];
    const cellJson = cell.getJson();
    const image = _.get(cellJson, 'imageOriginal') || _.get(cellJson, 'image');

    return this.enableProFeature() || (!this.enableProFeature() && !image);
  };

  showCopyImageNavIcon = () => {
    const cell = this.graph.getSelectionCells()[0];
    const cellJson = cell.getJson();
    const image = _.get(cellJson, 'imageOriginal') || _.get(cellJson, 'image');

    return (!this.enableProFeature() && image && this.expiredProIco) || null;
  };

  enableProFeature = () => {
    return (this.isPro() && !this.isProTrial()) || (this.isProTrial() && this.isProTrialActive());
  };

  showNavIcon = () => {
    return (this.isProTrial() && !this.isProTrialActive() && this.expiredProIco) || null;
  };

  getProTrialRowTooltipForCopy = () => {
    return !this.enabledCopyActivity() && this.trialExpiredMsg;
  };

  getProTrialRowTooltip = () => {
    return !this.enableProFeature() && this.trialExpiredMsg;
  };

  public getNoChangesLicenseTooltip = () => {
    return !this.permissionsService.hasChangeTicketsLicense() && this.notAvailableChangesLicenseMsg;
  };

  public getNoDataTablesLicenseTooltip = () => {
    return !this.permissionsService.hasDataTablesLicense() && this.notAvailableDataTablesLicenseMsg;
  };

  private getGenerateChangesTooltipText() {
    const {
      isPlayground,
      isProPlus,
      isMsEditor,
      consultantElementsGPT,
      hasDiagram2TicketLicense,
      hasChangeTicketsLicense
    } = this.permissionsService;

    switch (true) {
      case !this.enableProFeature():
        return this.trialExpiredMsg;

      case isPlayground():
        return this.notAvailableInPlaygroundMessage;

      case !isProPlus():
        return this.notAvailableOnCurrentPlanMsg;

      case !hasChangeTicketsLicense():
        return this.notAvailableChangesLicenseMsg;

      case hasDiagram2TicketLicense() || consultantElementsGPT():
        return false;

      case !isMsEditor():
        return this.notAvailableOnCurrentPlanMsg;

      default:
        return this.gtpFlagTurnedOffMsg;
    }
  }

  private getLinkExistingDiagramTooltip = () => {
    switch (true) {
      case this.isProTrial() && !this.isProTrialActive():
        return this.trialExpiredMsg;

      case !this.isPro():
        return this.notAvailableOnPlanMsg;

      default:
        return this.translateService.instant('HINTS.LINK_EXISTING_DIAGRAM_DD_HINT');
    }
  };

  isImage() {
    const cell = this.graph.getSelectionCells()[0];
    const cellJson = cell.getJson();

    return _.get(cellJson, 'imageOriginal') || _.get(cellJson, 'image');
  }

  get canDeleteSelected() {
    const selection = this.graph.getSelectionCells();

    if (this.graph.isEnabled()) {
      return selection.length > 0;
    } else {
      return selection.every(isSticker) && selection.every(c => this.stickersSvc.canForceEdit(c));
    }
  }

  get canvasNotEmpty() {
    const children = this.graph.model.getChildren(this.graph.getDefaultParent());

    return !!(children && children.length);
  }

  private updateContextBarOptions() {
    this.graph.contextBar?.updateOptions();
  }

  private registerDrilldownAction(config: DrilldownConfig): void {
    const {actionType, drilldownSubtype, q9Track} = config;

    this.hub.addTrackableAction(actionType, {
      funct: () => {
        const [cell] = this.graph.getSelectionCells();

        this.drilldownSvc.addNewDrilldown(cell, drilldownSubtype);
      },
      q9Track
    }).isEnabled = () => {
      const {allowedDrilldownSubtypes} = this.diagramTypeConfigService.generalSettings();
      const selection = this.graph.getSelectionCells();

      return (
        this.graph.isEnabled() &&
        selection.length === 1 &&
        isActivity(selection[0]) &&
        !hasDrilldownOrLinkedDiagram(selection[0]) &&
        allowedDrilldownSubtypes.includes(drilldownSubtype)
      );
    };
  }
}
